import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { ScoreJudge } from '../../services/round-scores';

interface BlockJudgesPickerProps {
  /** Everybody with the judge role at this competition. */
  judges: ScoreJudge[];
  /** Who the block is handed to now. Empty means every judge. */
  judgeIds: string[];
  /** Saves the new set; rejects to leave the picker as it was. */
  onChange: (judgeIds: string[]) => Promise<void>;
  disabled?: boolean;
}

/**
 * Which judges stand at one judge-only block.
 *
 * A wall with thirty problems and ten judges is run by handing each block to
 * the two or three people at it, so that each judge's phone shows their blocks
 * and nobody else's. This is where that is done: a popover of the
 * competition's judges, ticked per block, saved on every tick. Nobody ticked
 * means the block is open to every judge — the sensible reading of "we never
 * got round to assigning it", and what the button says in that case.
 */
export default function BlockJudgesPicker({ judges, judgeIds, onChange, disabled }: BlockJudgesPickerProps) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);

  const assigned = judges.filter((judge) => judgeIds.includes(judge.id));
  const label =
    assigned.length === 0
      ? t('gymAdmin.roundBlocks.judges.everyone')
      : assigned.map((judge) => judge.name).join(', ');

  const toggle = async (judgeId: string, checked: boolean) => {
    if (saving) return;
    const next = checked ? [...judgeIds, judgeId] : judgeIds.filter((id) => id !== judgeId);
    setSaving(true);
    try {
      await onChange(next);
    } catch {
      // The page has already said what went wrong; the ticks stay as they were.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-1.5 h-7 max-w-48 gap-1 px-2 text-xs font-normal"
          title={t('gymAdmin.roundBlocks.judges.hint')}
          data-testid="block-judges-trigger"
        >
          <Users className="size-3.5 shrink-0" aria-hidden />
          <span className="truncate">{label}</span>
          <ChevronDown className="size-3 shrink-0" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 space-y-1 p-2">
        <p className="px-1 pb-1 text-xs text-muted-foreground">
          {t('gymAdmin.roundBlocks.judges.hint')}
        </p>
        {judges.length === 0 && (
          <p className="px-1 py-2 text-sm text-muted-foreground">
            {t('gymAdmin.roundBlocks.judges.none')}
          </p>
        )}
        {judges.map((judge) => (
          <label
            key={judge.id}
            className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-accent"
            data-testid="block-judge-option"
          >
            <Checkbox
              checked={judgeIds.includes(judge.id)}
              disabled={saving}
              onCheckedChange={(checked) => toggle(judge.id, checked === true)}
              aria-label={judge.name}
            />
            <span className="truncate">{judge.name}</span>
          </label>
        ))}
        {saving && <Loader2 className="ml-1 size-4 animate-spin text-muted-foreground" />}
      </PopoverContent>
    </Popover>
  );
}
